import { Directive, Injector, Input, forwardRef } from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { InputLabelMixin } from "./mixins/input-label";
import { EmphasisMixin } from "./mixins/emphasis";
import { SizeMixin } from "./mixins/size";
import { RequiredMixin } from "./mixins/required";
import { DirectiveMixin } from "./mixins/mixin";
import { DisabledMixin } from "./mixins/disabled";
import { InputLabelStateMixin } from "./mixins/label-state";
import qcompare from "qcompare";

export const INPUT_LABEL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxInputDirective),
  multi: true,
};

const Mixins = InputLabelMixin(
  EmphasisMixin(SizeMixin(InputLabelStateMixin(RequiredMixin(DisabledMixin(DirectiveMixin))))),
);

@Directive({
  selector: "[bfx-input]",
  providers: [INPUT_LABEL_VALUE_ACCESSOR],
  exportAs: "bfxInput",
  standalone: false,
})
export class BfxInputDirective extends Mixins {
  @Input("bfx-input")
  directiveInput: Record<string, any>;

  get classes(): Record<string, boolean> {
    let previousValue = this._previousValuesMap.get("classes");
    let currentValue = {
      "bfx-input": true,
      "bfx-input-sm": this.sm,
      "bfx-input-md": this.md,
      "bfx-input-lg": this.lg,
      "bfx-input-lg-subtle": this.lg && this.subtle,
      "bfx-input-md-subtle": this.md && this.subtle,
      "bfx-input-sm-subtle": this.sm && this.subtle,
      "bfx-input-lg-bold": this.lg && this.bold,
      "bfx-input-md-bold": this.md && this.bold,
      "bfx-input-sm-bold": this.sm && this.bold,
    };

    if (!qcompare(previousValue, currentValue)) {
      this._previousValuesMap.set("classes", currentValue);
      return currentValue;
    }

    return previousValue;
  }

  get labelClasses(): Record<string, boolean> {
    let previousValue = this._previousValuesMap.get("labelClasses");
    let currentValue = {
      "bfx-input-label": true,
      "bfx-input-label-lg": this.lg,
      "bfx-input-label-md": this.md,
      "bfx-input-label-sm": this.sm,
      "bfx-input-label-disabled": this.inputLabelState == "disabled" || this.disabled,
      "bfx-input-label-enabled": this.inputLabelState == "enabled",
      "bfx-input-label-read-only": this.inputLabelState == "read-only",
    };

    if (!qcompare(previousValue, currentValue)) {
      this._previousValuesMap.set("labelClasses", currentValue);
      return currentValue;
    }

    return previousValue;
  }

  get innerClasses(): Record<string, boolean> {
    let previousValue = this._previousValuesMap.get("innerClasses");
    let currentValue = {
      "bfx-input-inner": true,
      "bfx-input-inner-lg": this.lg,
      "bfx-input-inner-md": this.md,
      "bfx-input-inner-sm": this.sm,
    };
    if (!qcompare(previousValue, currentValue)) {
      this._previousValuesMap.set("innerClasses", currentValue);
      return currentValue;
    }

    return previousValue;
  }

  constructor(public injector: Injector) {
    super(injector);
  }

  directiveHost(host: Record<string, any>) {
    let previousValue = this._previousValuesMap.get("directiveHost");
    let currentValue = {
      required: host.required,
      emphasis: host.emphasis,
      size: host.size,
      inputLabelText: host.inputLabelText,
      inputLabelState: host.inputLabelState,
      disabled: host.disabled,
    };

    if (!qcompare(previousValue, currentValue)) {
      this._previousValuesMap.set("directiveHost", currentValue);
      this.applyDirectiveInput(currentValue);
      return currentValue;
    }

    return previousValue;
  }
}
